/**
 * Pha C trên web quầy: "Sổ tiền" (hôm nay thu bao nhiêu · hoá đơn + huỷ · chốt két), "Việc của tôi"
 * của KTV, và thẻ Cấu hình "Hoa hồng" (quy tắc + bảng tháng). Tiền là chuỗi số nguyên đồng (ADR-026);
 * mọi con số do MÁY CHỦ tính, màn này chỉ hiện.
 */
import {
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  Plus,
  Printer,
  Receipt,
  ShoppingBag,
  Trash2,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import {
  type Api,
  type BangLuong,
  type BangSo,
  type HoaDon,
  type Ket,
  ngayVn,
  type QuyTacHoaHong,
  type ServiceOption,
  type ToiLaAi,
  tenKhach,
  tienVnd,
  type ViecCuaToi,
} from './api.ts'
import { TheBaoCao } from './BaoCao.tsx'
import { useTinhNang } from './cai-dat-tiem.ts'
import { taiCoHuy } from './chi-nhanh-dang-dung.ts'
import { DongTenTiem, useChiNhanh } from './chi-nhanh-dang-dung.tsx'
import { DieuHuongNgay } from './DayBoard.tsx'
import { loiCua, lucViet, OLyDo, OTien, useKhoaIdem } from './GoiVi.tsx'
import { DsHoaDonHuy } from './HoaDonHuy.tsx'
import { TheKho, ToBanLe } from './Kho.tsx'
import { doiThang, NutHoaHongThang, TheChiTieu } from './KhoanChi.tsx'
import { ToMayIn } from './MayIn.tsx'
import { inHoaDon, LoiMayIn } from './may-in.ts'
import { canNhanChiNhanh } from './so-tien-chi-nhanh.ts'
import { TheTienBoNgay } from './TienBo.tsx'
import { IN } from './text/in-hoa-don.ts'
import { K } from './text/kho.ts'
import { C } from './text/khoan-chi.ts'
import { S } from './text/so-tien.ts'
import { B } from './text/tien-bo.ts'
import { T } from './text/vi.ts'
import { CacCum, Rong, ThongBao, To, useLanTaiMoiNhat, Xuong } from './ui.tsx'

type The = 'tong' | 'bao-cao' | 'hoa-don' | 'huy' | 'ket' | 'chi' | 'kho'

/** "08:05" giờ VN của một mốc ISO. */
function gioVn(iso: string): string {
  return lucViet(iso).slice(11)
}

/** Lệch két nói bằng lời: "Thiếu 120.000đ so với sổ" / "Dư 30.000đ so với sổ" / "Khớp". */
function loiLech(so: string): string {
  if (so === '0') return S.ketKhop
  return so.startsWith('-') ? S.ketThieu(tienVnd(so.slice(1))) : S.ketDu(tienVnd(so))
}

/** Tiền có dấu: "+50.000đ" / "−50.000đ" / "0đ". */
function tienDau(so: string): string {
  if (so.startsWith('-')) return `−${tienVnd(so.slice(1))}`
  return so === '0' ? tienVnd('0') : `+${tienVnd(so)}`
}

export function TrangSoTien({
  toi,
  api,
  onMoCauHinhSp,
  theDau,
  ngayDau,
}: {
  toi: ToiLaAi
  api: Api
  onMoCauHinhSp?: () => void
  /** Mở từ tin chuông (hoá đơn bị huỷ / két lệch): đúng thẻ, đúng ngày. */
  theDau?: 'huy' | 'ket'
  ngayDau?: string
}) {
  const xemDoanhThu = toi.quyen.includes('xem.doanh-thu')
  const [the, setThe] = useState<The>(theDau ?? (xemDoanhThu ? 'tong' : 'hoa-don'))
  const [ngay, setNgay] = useState(ngayDau ?? ngayVn())
  // Công tắc tính năng (ADR-074): tắt Kho / Đếm két thì không có thẻ đó.
  const coKho = useTinhNang('kho')
  const coKet = useTinhNang('dem-ket')
  // KH-GĐ2 5.3: quầy ghi khoản chi (quyền thu tiền); tiền lời chỉ người xem được doanh thu.
  const coChi = useTinhNang('khoan-chi') && toi.quyen.includes('thu-tien')
  const cacThe: { ma: The; ten: string }[] = [
    ...(xemDoanhThu ? [{ ma: 'tong' as const, ten: S.tabTongQuan }] : []),
    ...(xemDoanhThu ? [{ ma: 'bao-cao' as const, ten: S.tabBaoCao }] : []),
    { ma: 'hoa-don', ten: S.tabHoaDon },
    // KH-GĐ2 7.2.5: hoá đơn bị huỷ theo ngày huỷ — người xem được doanh thu.
    ...(xemDoanhThu ? [{ ma: 'huy' as const, ten: S.tabHuy }] : []),
    ...(coKet ? [{ ma: 'ket' as const, ten: S.tabKet }] : []),
    ...(coChi ? [{ ma: 'chi' as const, ten: C.tab }] : []),
    ...(coKho ? [{ ma: 'kho' as const, ten: K.tab }] : []),
  ]
  return (
    <>
      <header className="dau-man">
        <div className="ten-man">
          <DongTenTiem tenTiem={toi.tenTiem} theoChiNhanh={the === 'ket' || the === 'kho'} />
          <h1>{S.page}</h1>
        </div>
      </header>
      <main className="than-man so-tien">
        <div className="hang-chip" role="tablist" aria-label={S.page}>
          {cacThe.map((t) => (
            <button
              key={t.ma}
              type="button"
              role="tab"
              aria-selected={the === t.ma}
              className={`chip ${the === t.ma ? 'dang-chon' : ''}`}
              onClick={() => setThe(t.ma)}
            >
              {t.ten}
            </button>
          ))}
        </div>
        {/* Lễ tân chỉ xem hoá đơn HÔM NAY (máy chủ chặn ngày khác) — không hiện nút lùi ngày cho họ. */}
        {(the === 'tong' ||
          the === 'huy' ||
          (the === 'hoa-don' && toi.quyen.includes('xem.doanh-thu'))) && (
          <DieuHuongNgay date={ngay} onDoi={setNgay} />
        )}
        {the === 'tong' && (
          <TongQuanNgay api={api} ngay={ngay} choDao={toi.quyen.includes('hoan-dao-tien')} />
        )}
        {the === 'bao-cao' && <TheBaoCao />}
        {the === 'hoa-don' && (
          <DsHoaDon
            api={api}
            ngay={ngay}
            tenTiem={toi.tenTiem}
            // Huỷ hoá đơn NGÀY TRƯỚC chỉ chủ tiệm (máy chủ chặn) — quản lý chỉ huỷ được của hôm nay.
            choHuy={
              toi.quyen.includes('hoan-dao-tien') && (toi.toi.vai === 'owner' || ngay === ngayVn())
            }
          />
        )}
        {the === 'huy' && xemDoanhThu && <DsHoaDonHuy api={api} ngay={ngay} />}
        {the === 'ket' && coKet && <TheKet api={api} />}
        {the === 'chi' && coChi && <TheChiTieu api={api} toi={toi} />}
        {the === 'kho' && coKho && (
          <TheKho
            api={api}
            choKiemKe={toi.quyen.includes('hoan-dao-tien')}
            {...(onMoCauHinhSp === undefined ? {} : { onMoCauHinh: onMoCauHinhSp })}
          />
        )}
      </main>
    </>
  )
}

// ------------------------------------------------------------------ tổng quan một ngày

function TongQuanNgay({ api, ngay, choDao }: { api: Api; ngay: string; choDao: boolean }) {
  const coHoaHong = useTinhNang('hoa-hong')
  const coTienBo = useTinhNang('tien-bo')
  const coDemKet = useTinhNang('dem-ket')
  // Số ở đây là của CẢ TIỆM (không theo chi nhánh đang dùng): tiệm nhiều chi nhánh được nói rõ một câu.
  const { nhieu: nhieuCn, ds: dsCn } = useChiNhanh()
  const [bs, setBs] = useState<BangSo | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    setBs(null)
    setLoi(null)
    let huy = false
    void api
      .bangSo(ngay)
      .then((r) => {
        if (!huy) setBs(r)
      })
      .catch((e: unknown) => {
        if (!huy) setLoi(loiCua(e))
      })
    return () => {
      huy = true
    }
  }, [api, ngay])
  if (loi !== null) return <ThongBao loai="loi">{loi}</ThongBao>
  if (bs === null) return <Xuong cao={180} />
  const dong: [string, string][] = [
    [S.dvTienMat, bs.thuDichVu.tienMatVnd],
    [S.dvChuyenKhoan, bs.thuDichVu.chuyenKhoanVnd],
    [S.dvThe, bs.thuDichVu.theVnd],
    [S.banGoi, bs.banGoiVnd],
    [S.napVi, bs.napViVnd],
    ...(bs.nhanCocVnd !== undefined && bs.nhanCocVnd !== '0'
      ? [[S.nhanCoc, bs.nhanCocVnd] as [string, string]]
      : []),
  ]
  return (
    <div className="luoi-hom-nay">
      <div>
        <section className="the the-dem so-to">
          <span className="nhan-muc">{S.tienVao}</span>
          <b>{tienVnd(bs.tienVaoVnd)}</b>
          <span className="chu-phu">{S.tienVaoPhu}</span>
          {nhieuCn && <p className="chu-phu">{S.soCaTiem(dsCn?.length ?? 0)}</p>}
          {nhieuCn && coDemKet && <p className="chu-phu">{S.soKetTungCn}</p>}
          <ul className="ds-so">
            {dong.map(([ten, so]) => (
              <li key={ten}>
                <span>{ten}</span>
                <b>{tienVnd(so)}</b>
              </li>
            ))}
          </ul>
          {bs.banSanPhamVnd !== '0' && (
            <p className="chu-phu">{S.trongDoSanPham(tienVnd(bs.banSanPhamVnd))}</p>
          )}
          {bs.thuDichVu.viVnd !== '0' && (
            <p className="chu-phu">{S.viDong(tienVnd(bs.thuDichVu.viVnd))}</p>
          )}
          {bs.thuDichVu.cocVnd !== undefined && bs.thuDichVu.cocVnd !== '0' && (
            <p className="chu-phu">{S.cocDong(tienVnd(bs.thuDichVu.cocVnd))}</p>
          )}
        </section>
        {coTienBo && <TheTienBoNgay api={api} ngay={ngay} choDao={choDao} />}
        {bs.thuChuyenKhoan !== undefined && bs.thuChuyenKhoan.length > 0 && (
          <section className="the the-dem so-to">
            <span className="nhan-muc">{S.thuCkTitle}</span>
            <span className="chu-phu">{S.thuCkPhu}</span>
            <ul className="ds-so">
              {bs.thuChuyenKhoan.map((d) => (
                <li key={`${d.luc}-${d.soHoaDon}-${d.soTienVnd}`}>
                  <span>
                    {gioVn(d.luc)} · {d.maLich ?? d.soHoaDon}
                    {d.bo === true && ` · ${S.thuCkBo(d.cach)}`}{' '}
                    <span className="chu-phu">· {S.cocCkNguoi(d.nguoi)}</span>
                  </span>
                  <b>{tienVnd(d.soTienVnd)}</b>
                </li>
              ))}
            </ul>
          </section>
        )}
        {bs.cocChuyenKhoan !== undefined && bs.cocChuyenKhoan.length > 0 && (
          <section className="the the-dem so-to">
            <span className="nhan-muc">{S.cocCkTitle}</span>
            <span className="chu-phu">{S.cocCkPhu}</span>
            <ul className="ds-so">
              {bs.cocChuyenKhoan.map((d) => (
                <li key={`${d.luc}-${d.maLich}-${d.loai}-${d.soTienVnd}`}>
                  <span>
                    {gioVn(d.luc)} · {d.maLich}{' '}
                    <span className="chu-phu">
                      · {S.cocCkLoai[d.loai] ?? d.loai} · {S.cocCkNguoi(d.nguoi)}
                    </span>
                  </span>
                  <b>{tienVnd(d.soTienVnd)}</b>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
      <div>
        <div className="luoi-2-rong">
          <section className="the the-dem so-lieu">
            <span className="nhan-muc">{S.lich}</span>
            <b>{bs.lich.tong}</b>
            <span className="chu-phu">
              <CacCum cac={S.lichDongCum(bs.lich.xong, bs.lich.tong, bs.lich.khongDen)} />
            </span>
          </section>
          <section className="the the-dem so-lieu">
            <span className="nhan-muc">{S.giamGia}</span>
            <b>{tienVnd(bs.giamGiaVnd)}</b>
            {bs.soHuy > 0 && <span className="chu-phu">{S.hoaDonHuy(bs.soHuy)}</span>}
          </section>
        </div>
        {coHoaHong && (
          <section className="the the-dem so-to">
            <span className="nhan-muc">{S.hoaHongThang}</span>
            {bs.hoaHongThang.length === 0 ? (
              <p className="chu-phu">{S.chuaCoHoaHong}</p>
            ) : (
              <ul className="ds-so">
                {bs.hoaHongThang.map((k) => (
                  <li key={k.ten}>
                    <span>
                      <CacCum cac={[k.ten, S.soDichVu(k.soDichVu)]} />
                    </span>
                    <b>{tienVnd(k.tienVnd)}</b>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </div>
  )
}

// ------------------------------------------------------------------ hoá đơn trong ngày

function DsHoaDon({
  api,
  ngay,
  choHuy,
  tenTiem,
}: {
  api: Api
  ngay: string
  choHuy: boolean
  tenTiem: string
}) {
  const coKho = useTinhNang('kho')
  const [ds, setDs] = useState<HoaDon[] | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [bao, setBao] = useState<string | null>(null)
  const [mo, setMo] = useState<HoaDon | null>(null)
  const [banLe, setBanLe] = useState(false)
  // ADR-101: chi nhánh lấy từ nguồn chung; tờ Bán lẻ mặc định chi nhánh đang dùng (vẫn có ô chọn riêng khi > 1 chi nhánh).
  const { ds: chiNhanh, nhieu: nhieuCn } = useChiNhanh()
  const lanMoi = useLanTaiMoiNhat()
  const nap = useCallback(() => {
    setLoi(null)
    const conMoi = lanMoi()
    void api
      .hoaDonNgay(ngay)
      .then((r) => {
        if (conMoi()) setDs(r.hoaDon)
      })
      .catch((e: unknown) => {
        if (conMoi()) setLoi(loiCua(e))
      })
  }, [api, ngay, lanMoi])
  useEffect(() => {
    setDs(null)
    nap()
  }, [nap])

  return (
    <>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {coKho && chiNhanh !== null && chiNhanh.length > 0 && (
        <div className="hang-chip">
          <button type="button" className="nut nut-vien nut-nho" onClick={() => setBanLe(true)}>
            <ShoppingBag size={16} />
            {K.banLe}
          </button>
        </div>
      )}
      {ds === null ? (
        loi === null && <Xuong cao={160} />
      ) : ds.length === 0 ? (
        <div className="the">
          <Rong icon={<Receipt size={26} />} tieuDe={S.chuaCoHoaDon} moTa={S.chuaCoHoaDonPhu} />
        </div>
      ) : (
        <DanhSachHoaDon ds={ds} nhieu={nhieuCn} onMo={setMo} />
      )}
      {banLe && chiNhanh !== null && chiNhanh.length > 0 && (
        <ToBanLe
          api={api}
          onDong={() => setBanLe(false)}
          onXong={(cau) => {
            setBanLe(false)
            setBao(cau)
            nap()
          }}
        />
      )}
      {mo !== null && (
        <ToHoaDon
          api={api}
          hd={mo}
          tenTiem={tenTiem}
          choHuy={choHuy}
          onDong={() => setMo(null)}
          onDaHuy={(so, boDaDao) => {
            setMo(null)
            setBao(
              boDaDao.length === 0
                ? S.daHuyXong(so)
                : `${S.daHuyXong(so)}. ${S.daHuyBo(boDaDao.map((b) => S.boDong(b.ten, tienVnd(b.soTienVnd))).join(', '))}`,
            )
            nap()
          }}
        />
      )}
    </>
  )
}

/**
 * Danh sách hoá đơn trong ngày (tách ra để vẽ tĩnh trong test). Tiệm nhiều chi nhánh (hoặc ngày có hoá đơn của ≥ 2 chi nhánh)
 * ghi tên chi nhánh sau số hoá đơn: số đánh theo chi nhánh nên hai tờ có thể trùng số (ADR-044).
 */
export function DanhSachHoaDon({
  ds,
  nhieu,
  onMo,
}: {
  ds: HoaDon[]
  nhieu: boolean
  onMo: (h: HoaDon) => void
}) {
  const ghiCn = canNhanChiNhanh(
    ds.map((h) => h.inThongTin.chiNhanh),
    nhieu,
  )
  return (
    <ul className="the ds-hoa-don">
      {ds.map((h) => (
        <li key={h.id}>
          <button
            type="button"
            className={`dong-hoa-don ${h.status === 'void' ? 'da-huy' : ''}${ghiCn ? ' co-chi-nhanh' : ''}`}
            onClick={() => onMo(h)}
          >
            <span className="gio">{gioVn(h.paidAt)}</span>
            {/* `chu-cn` (chỉ khi ghi chi nhánh): mọi dòng chữ của khối được xuống dòng, không cắt "HD26-000…" (soát giao diện B1). */}
            <span className={ghiCn ? 'chu chu-cn' : 'chu'}>
              <b>{h.khach === null ? S.khachLe : tenKhach(h.khach)}</b>
              {/* Khi ghi chi nhánh, khối được phép xuống dòng: số hoá đơn nằm trong `so-hd` (nowrap) để "HD26-" / "000001" không bị bẻ ở dấu gạch (N-a).
                  Tiệm một chi nhánh: markup y từng byte như cũ. */}
              {ghiCn ? (
                <span>
                  <span className="so-hd">{h.number}</span> ·{' '}
                  {h.dong.map((d) => d.label).join(', ')}
                </span>
              ) : (
                <span>
                  {h.number} · {h.dong.map((d) => d.label).join(', ')}
                </span>
              )}
              {/* Tên chi nhánh là DÒNG PHỤ riêng (xuống dòng, không "…"): dòng "số · dịch vụ" giữ y như bản cũ (soát giao diện B1). */}
              {ghiCn && h.inThongTin.chiNhanh ? (
                <span className="ten-cn-hd">{h.inThongTin.chiNhanh}</span>
              ) : null}
            </span>
            <span className="tien">
              <b>{tienVnd(h.totalVnd)}</b>
              <span>{h.status === 'void' ? S.daHuy : (S.cach[h.payMethod] ?? '')}</span>
            </span>
          </button>
        </li>
      ))}
    </ul>
  )
}

function ToHoaDon({
  api,
  hd,
  tenTiem,
  choHuy,
  onDong,
  onDaHuy,
}: {
  api: Api
  hd: HoaDon
  tenTiem: string
  choHuy: boolean
  onDong: () => void
  onDaHuy: (so: string, boDaDao: { ten: string; soTienVnd: string }[]) => void
}) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [huy, setHuy] = useState(false)
  const [lyDo, setLyDo] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [dangIn, setDangIn] = useState(false)
  const [baoIn, setBaoIn] = useState<string | null>(null)
  const [moMayIn, setMoMayIn] = useState(false)
  async function inNgay() {
    setDangIn(true)
    setBaoIn(null)
    setLoi(null)
    try {
      await inHoaDon(hd, tenTiem)
      setBaoIn(IN.daIn)
    } catch (e) {
      // Chưa nối / mất kết nối máy in: nói rõ và mở luôn tờ chọn máy in, đỡ một chạm.
      if (e instanceof LoiMayIn && e.loai !== 'huy') {
        setLoi(e.loai === 'khong-ghi-duoc' ? IN.khongGhiDuoc : IN.khongTimThayCong)
        setMoMayIn(true)
      }
    } finally {
      setDangIn(false)
    }
  }
  async function huyNgay() {
    setDang(true)
    setLoi(null)
    try {
      const r = await api.huyHoaDon(hd.id, lyDo.trim(), khoa)
      lamMoiKhoa()
      onDaHuy(r.number, r.boDaDao ?? [])
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  return (
    <To nhan={hd.number} onDong={onDong}>
      <header className="to-dau">
        <div style={{ flex: 1 }}>
          <h2>{hd.number}</h2>
          <p className="chu-phu">
            {lucViet(hd.paidAt)} · {hd.khach === null ? S.khachLe : tenKhach(hd.khach)}
            {hd.thuNgan !== null && ` · ${S.thuNgan(hd.thuNgan)}`}
          </p>
        </div>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        {hd.status === 'void' && (
          <ThongBao loai="canh-bao">
            {S.daHuy}
            {hd.voidReason !== null && ` — ${S.lyDoHuy(hd.voidReason)}`}
          </ThongBao>
        )}
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        {baoIn !== null && (
          <ThongBao loai="thanh-cong" onDong={() => setBaoIn(null)}>
            {baoIn}
          </ThongBao>
        )}
        <ul className="ds-so">
          {hd.dong.map((d) => (
            <li key={`${d.label}-${d.priceVnd}-${d.ktv ?? ''}`}>
              <span>
                {d.label}
                {d.ktv !== null && <span className="chu-phu"> · {d.ktv}</span>}
              </span>
              <b>{d.paidBy === 'package' ? S.truGoi : tienVnd(d.priceVnd)}</b>
            </li>
          ))}
          {hd.discountVnd !== '0' && (
            <>
              <li>
                <span>{S.tamTinh}</span>
                <b>{tienVnd(hd.subtotalVnd)}</b>
              </li>
              <li>
                <span>{S.giam(hd.discountReason ?? '')}</span>
                <b>−{tienVnd(hd.discountVnd)}</b>
              </li>
            </>
          )}
          <li className="dong-tong">
            <span>{S.tong}</span>
            <b>{tienVnd(hd.totalVnd)}</b>
          </li>
        </ul>
        {hd.traBang.length > 0 && (
          <div className="muc-to">
            <h3>{S.traBang}</h3>
            <ul className="ds-so">
              {hd.traBang.map((t) => (
                <li key={t.method}>
                  <span>{S.cach[t.method] ?? t.method}</span>
                  <b>{tienVnd(t.soVnd)}</b>
                </li>
              ))}
            </ul>
          </div>
        )}
        {choHuy && hd.status === 'paid' && !huy && (
          <button type="button" className="nut nut-nhat" onClick={() => setHuy(true)}>
            {S.huyHoaDon}
          </button>
        )}
        {huy && (
          <>
            <ThongBao loai="canh-bao">{S.huyGiaiThich}</ThongBao>
            <OLyDo giaTri={lyDo} onDoi={setLyDo} goiY={S.huyLyDo} />
          </>
        )}
      </div>
      {moMayIn && <ToMayIn tenTiem={tenTiem} onDong={() => setMoMayIn(false)} />}
      {hd.status === 'paid' && !huy && (
        <footer className="to-chan">
          <button
            type="button"
            className="nut nut-vien"
            aria-label={IN.nutMayIn}
            onClick={() => setMoMayIn(true)}
          >
            {IN.nutMayIn}
          </button>
          <button
            type="button"
            className="nut nut-chinh nut-lon"
            style={{ flex: 1 }}
            disabled={dangIn}
            onClick={() => void inNgay()}
          >
            <Printer size={18} />
            {dangIn ? IN.dangIn : IN.nutIn}
          </button>
        </footer>
      )}
      {choHuy && hd.status === 'paid' && huy && (
        <footer className="to-chan">
          <button type="button" className="nut nut-vien" onClick={() => setHuy(false)}>
            {S.giuLai}
          </button>
          <button
            type="button"
            className="nut nut-nguy-dac"
            style={{ flex: 1 }}
            disabled={lyDo.trim().length < 2 || dang}
            onClick={() => void huyNgay()}
          >
            {S.huyNut}
          </button>
        </footer>
      )}
    </To>
  )
}

// ------------------------------------------------------------------ két

function TheKet({ api }: { api: Api }) {
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  // ADR-101: két của chi nhánh đang dùng (nút chọn chi nhánh thay `<select>` cũ ở đầu thẻ).
  const { ds: cn, id: branchId } = useChiNhanh()
  const [ket, setKet] = useState<Ket | null>(null)
  const [dem, setDem] = useState('')
  const [giuLai, setGiuLai] = useState('')
  const [note, setNote] = useState('')
  /** Chốt két là KHÔNG sửa được: hỏi lại một lần (ui-critic v11). */
  const [hoiChot, setHoiChot] = useState(false)
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  const [bao, setBao] = useState<string | null>(null)

  /** Trả hàm dọn: đổi chi nhánh thì két của chi nhánh cũ về muộn bị bỏ (vòng 1 A — chuông `ket_lech` có thể đổi chi nhánh sau khi màn mở). */
  const nap = useCallback((): (() => void) => {
    if (branchId === '') return () => undefined
    return taiCoHuy(api.ket(branchId), setKet, (e) => setLoi(loiCua(e)))
  }, [api, branchId])
  useEffect(() => {
    // Đổi chi nhánh: bỏ số đếm / lý do đang gõ dở của chi nhánh cũ — không chốt nhầm két chi nhánh khác.
    // Khoá chống lặp (`khoa`) KHÔNG xoá (bài học ADR-100).
    setKet(null)
    setDem('')
    setGiuLai('')
    setNote('')
    setHoiChot(false)
    setLoi(null)
    setBao(null)
    return nap()
  }, [nap])

  if (loi !== null && ket === null) return <ThongBao loai="loi">{loi}</ThongBao>
  if (ket === null || cn === null) return <Xuong cao={220} />

  const lech = dem === '' ? null : BigInt(dem) - BigInt(ket.canCoVnd)
  // KH-GĐ2 7.3.4: lệch bao nhiêu cũng ghi lý do (máy chủ chặn + báo chủ tiệm).
  const coLech = lech !== null && lech !== 0n
  const giuLaiQua = giuLai !== '' && dem !== '' && BigInt(giuLai) > BigInt(dem)
  const hopLe = dem !== '' && !giuLaiQua && (!coLech || note.trim().length >= 2)

  async function chot() {
    setDang(true)
    setLoi(null)
    try {
      const r = await api.chotKet(
        {
          branchId,
          demVnd: dem,
          giuLaiVnd: giuLai === '' ? '0' : giuLai,
          note: note.trim() === '' ? null : note.trim(),
        },
        khoa,
      )
      lamMoiKhoa()
      setBao(S.ketDaChot(loiLech(r.lechVnd)))
      setHoiChot(false)
      setDem('')
      setGiuLai('')
      setNote('')
      nap()
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }

  const ct: [string, string, boolean][] = [
    [
      ket.dauKyBoi != null ? S.ketDauKyBoi(ket.dauKyBoi) : S.ketDauKy,
      ket.chiTiet.dauKyVnd ?? '0',
      false,
    ],
    [S.ketThuDichVu, ket.chiTiet.thuDichVuVnd, false],
    [S.ketBanGoi, ket.chiTiet.banGoiVnd, false],
    [S.ketNapVi, ket.chiTiet.napViVnd, false],
    [S.ketHoanVi, ket.chiTiet.traHoanViVnd, true],
    [S.ketHoanGoi, ket.chiTiet.traHoanGoiVnd, true],
    [S.ketTraBo, ket.chiTiet.traBoVnd ?? '0', true],
    [S.ketChi, ket.chiTiet.chiTienMatVnd ?? '0', true],
  ]
  // Cọc tiền mặt ròng có thể âm (hoàn cọc đã nhận trước lần chốt) → hiện là khoản trả ra.
  const coc = BigInt(ket.chiTiet.nhanCocVnd ?? '0')
  if (coc > 0n) ct.push([S.ketNhanCoc, coc.toString(), false])
  if (coc < 0n) ct.push([S.ketHoanCoc, (-coc).toString(), true])
  return (
    <>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      <section className="the the-dem so-to">
        <span className="nhan-muc">{S.ketCanCo}</span>
        <b>{tienVnd(ket.canCoVnd)}</b>
        <span className="chu-phu">
          {ket.tuLanChotTruoc ? S.ketTuLanChot(lucViet(ket.tu)) : S.ketTuDauLanDau}
        </span>
        <ul className="ds-so">
          {ct
            .filter(([, so]) => so !== '0')
            .map(([ten, so, ra]) => {
              // Số âm (vd. huỷ hoá đơn tiền mặt đã thu TRƯỚC lần chốt → trả lại khách từ két): đảo chiều, không in "+-"
              // (ui-critic GĐ4 BLOCKER 2).
              const am = so.startsWith('-')
              return (
                <li key={ten}>
                  <span>{ten}</span>
                  <b>
                    {ra !== am ? '−' : '+'}
                    {tienVnd(am ? so.slice(1) : so)}
                  </b>
                </li>
              )
            })}
        </ul>
      </section>
      <section className="the the-dem">
        <label className="nhan" htmlFor="ket-dem">
          {S.ketDem}
        </label>
        <OTien id="ket-dem" giaTri={dem} onDoi={setDem} goiY={S.ketDemGoiY} />
        {/* QĐ 02/10 #12 + ui-critic v32: hướng dẫn đếm ngay dưới ô đếm, không đếm cả tiền cũ rồi bị báo "Dư". */}
        {!ket.tuLanChotTruoc && <ThongBao loai="canh-bao">{S.ketLanDauDem}</ThongBao>}
        {/* KH-GĐ2 7.3.4: chênh lệch là MỘT con số lớn. */}
        {lech !== null && (
          <p className={`so-lech-ket ${coLech ? 'chu-loi' : 'chu-khop'}`}>
            {loiLech(lech.toString())}
          </p>
        )}
        {coLech && (
          <div className="truong" style={{ marginTop: 8 }}>
            <label className="nhan" htmlFor="ket-ly-do">
              {S.ketLyDo}
            </label>
            <input
              id="ket-ly-do"
              className="o-nhap"
              placeholder={S.ketLyDoGoiY}
              value={note}
              maxLength={300}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        )}
        <div className="truong" style={{ marginTop: 8 }}>
          <label className="nhan" htmlFor="ket-giu-lai">
            {S.ketGiuLai}
          </label>
          <OTien id="ket-giu-lai" giaTri={giuLai} onDoi={setGiuLai} goiY={S.ketGiuLaiGoiY} />
          <p className={`ghi-chu-16 ${giuLaiQua ? 'chu-loi' : ''}`}>
            {giuLaiQua ? S.ketGiuLaiQua : S.ketGiuLaiPhu}
          </p>
        </div>
        {hoiChot ? (
          <>
            <ThongBao loai="canh-bao">{S.ketHoiLai(tienVnd(dem === '' ? '0' : dem))}</ThongBao>
            <div className="hang-chip" style={{ marginTop: 12 }}>
              <button type="button" className="nut nut-vien" onClick={() => setHoiChot(false)}>
                {S.demLai}
              </button>
              <button
                type="button"
                className="nut nut-chinh"
                style={{ flex: 1 }}
                disabled={!hopLe || dang}
                onClick={() => void chot()}
              >
                {S.ketChotThat}
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            className="nut nut-chinh nut-lon"
            style={{ marginTop: 12, width: '100%' }}
            disabled={!hopLe || dang}
            onClick={() => setHoiChot(true)}
          >
            {S.ketChot}
          </button>
        )}
      </section>
      <section className="the the-dem">
        <h2>{S.ketLichSu}</h2>
        {ket.lichSu.length === 0 ? (
          <p className="chu-phu">{S.ketChuaChot}</p>
        ) : (
          <ul className="ds-so">
            {/* Hai dòng (ui-critic GĐ4): giờ + lệch ở trên, chi tiết ở dưới — điện thoại không ép thành cột hẹp. */}
            {ket.lichSu.map((k) => (
              <li key={k.id} className="dong-chot-ket">
                <div className="hang-chot-ket">
                  <span>{lucViet(k.luc)}</span>
                  <b className={k.lechVnd === '0' ? '' : 'chu-loi'}>{loiLech(k.lechVnd)}</b>
                </div>
                <p className="ghi-chu-16">
                  {S.ketDongLichSu(tienVnd(k.canCoVnd), tienVnd(k.demVnd))}
                  {k.giuLaiVnd !== undefined &&
                    k.giuLaiVnd !== '0' &&
                    ` · ${S.ketDeLai(tienVnd(k.giuLaiVnd))}`}
                  {k.nguoi !== null && ` · ${k.nguoi}`}
                  {k.note !== null && ` · ${k.note}`}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}

// ------------------------------------------------------------------ việc của tôi (KTV)

export function TrangCuaToi({ toi, api }: { toi: ToiLaAi; api: Api }) {
  const coHoaHong = useTinhNang('hoa-hong')
  const [v, setV] = useState<ViecCuaToi | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  useEffect(() => {
    void api
      .viecCuaToi()
      .then(setV)
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api])
  return (
    <>
      <header className="dau-man">
        <div className="ten-man">
          <DongTenTiem tenTiem={toi.tenTiem} theoChiNhanh={false} />
          <h1>{S.navCuaToi}</h1>
        </div>
      </header>
      <main className="than-man so-tien">
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        {v === null ? (
          loi === null && <Xuong cao={200} />
        ) : (
          <>
            <section className="the the-dem so-to">
              {coHoaHong && (
                <>
                  <span className="nhan-muc">{S.hoaHongCuaToi}</span>
                  <b>{tienVnd(v.hoaHongThangVnd)}</b>
                  <span className="chu-phu">{S.hoaHongGhiChu}</span>
                </>
              )}
              <span className="chu-phu">
                <CacCum cac={S.soKhachDaLamCum(v.soKhachHomNay, v.soKhachThang)} />
              </span>
              {/* KH-GĐ2 5.2.5: bo của chính mình (null khi tiệm tắt "Tiền bo"). */}
              {v.tienBo != null && (
                <span className="chu-phu">
                  {B.cuaToi(tienVnd(v.tienBo.homNayVnd), tienVnd(v.tienBo.thangVnd))}
                  {v.tienBo.dangNoVnd !== '0' && BigInt(v.tienBo.dangNoVnd) > 0n && (
                    <> · {B.cuaToiNo(tienVnd(v.tienBo.dangNoVnd))}</>
                  )}
                </span>
              )}
            </section>
            <section className="the the-dem">
              <h2>
                <CalendarClock size={18} /> {S.homNayLam}
              </h2>
              {v.viec.length === 0 ? (
                <p className="chu-phu">{S.chuaCoViec}</p>
              ) : (
                <ul className="ds-so">
                  {v.viec.map((x) => (
                    <li key={`${x.appointmentId}-${x.tu}`}>
                      <span>
                        <b>
                          {gioVn(x.tu)}–{gioVn(x.den)}
                        </b>{' '}
                        {x.dichVu}
                        <span className="chu-phu">
                          {' '}
                          · {x.khach === null ? S.khachLe : tenKhach(x.khach)}{' '}
                        </span>
                        <span className={`vien-tt tt-${x.status}`}>
                          {S.trangThai[x.status] ?? x.status}
                        </span>
                        <span></span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            {coHoaHong && v.hoaHong.length > 0 && (
              <section className="the the-dem">
                <h2>{S.tungDichVu}</h2>
                <ul className="ds-so">
                  {v.hoaHong.map((h) => (
                    <li key={`${h.luc}-${h.dichVu}-${h.tienVnd}`}>
                      <span>
                        {lucViet(h.luc).slice(0, 5)} · {h.dichVu}
                        {h.dao && <span className="chu-phu"> · {S.dongDao}</span>}
                      </span>
                      <b>{tienDau(h.tienVnd)}</b>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </>
        )}
      </main>
    </>
  )
}

// ------------------------------------------------------------------ Cấu hình: hoa hồng + bảng tháng

function thangTruoc(): string {
  const [y, m] = ngayVn().slice(0, 7).split('-')
  const nam = Number.parseInt(y ?? '2026', 10)
  const thang = Number.parseInt(m ?? '1', 10)
  return thang === 1 ? `${nam - 1}-12` : `${nam}-${String(thang - 1).padStart(2, '0')}`
}

/** "12,5" → 1250 bp; chấp nhận dấu phẩy kiểu Việt. */
function phanTramRaBp(pt: string): number {
  const [nguyen = '0', le = ''] = pt.replace(',', '.').split('.')
  return (
    Number.parseInt(nguyen === '' ? '0' : nguyen, 10) * 100 +
    Number.parseInt(`${le}00`.slice(0, 2), 10)
  )
}

function bpRaPhanTram(bp: number): string {
  const nguyen = Math.floor(bp / 100)
  const le = bp % 100
  return le === 0 ? String(nguyen) : `${nguyen},${String(le).padStart(2, '0').replace(/0$/, '')}`
}

export function TheHoaHongCauHinh({ api }: { api: Api }) {
  const [dv, setDv] = useState<ServiceOption[]>([])
  const [macDinh, setMacDinh] = useState<string | null>(null)
  const [truocGiam, setTruocGiam] = useState(true)
  const [rieng, setRieng] = useState<{ serviceVariantId: string; pt: string }[]>([])
  const [bao, setBao] = useState<string | null>(null)
  const [loi, setLoi] = useState<string | null>(null)

  useEffect(() => {
    void Promise.all([api.quyTacHoaHong(), api.services()])
      .then(([q, s]) => {
        setMacDinh(bpRaPhanTram(q.macDinhBp))
        setTruocGiam(q.truocGiam === true)
        setRieng(
          q.rieng.map((r) => ({
            serviceVariantId: r.serviceVariantId,
            pt: bpRaPhanTram(r.rateBp),
          })),
        )
        setDv(s.services)
      })
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api])

  async function luu() {
    if (macDinh === null) return
    setLoi(null)
    try {
      const than: QuyTacHoaHong = {
        truocGiam,
        macDinhBp: phanTramRaBp(macDinh),
        rieng: rieng
          .filter((r) => r.serviceVariantId !== '')
          .map((r) => ({ serviceVariantId: r.serviceVariantId, rateBp: phanTramRaBp(r.pt) })),
      }
      const q = await api.luuQuyTacHoaHong(than)
      setMacDinh(bpRaPhanTram(q.macDinhBp))
      setTruocGiam(q.truocGiam === true)
      setRieng(
        q.rieng.map((r) => ({ serviceVariantId: r.serviceVariantId, pt: bpRaPhanTram(r.rateBp) })),
      )
      setBao(S.daLuuHoaHong)
    } catch (e) {
      setLoi(loiCua(e))
    }
  }

  const locPt = (s: string) => s.replace(/[^\d,.]/g, '').slice(0, 5)
  const daChon = new Set(rieng.map((r) => r.serviceVariantId))

  return (
    <div className="goi-vi-cau-hinh">
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && (
        <ThongBao loai="loi" onDong={() => setLoi(null)}>
          {loi}
        </ThongBao>
      )}
      <section className="the the-dem">
        <h2>{S.hoaHongTitle}</h2>
        <p className="chu-phu">{S.hoaHongHint}</p>
        {macDinh === null ? (
          <Xuong cao={80} />
        ) : (
          <>
            <div className="truong o-phan-tram" style={{ maxWidth: 240, marginTop: 12 }}>
              <label className="nhan" htmlFor="hh-mac-dinh">
                {S.macDinh}
              </label>
              <input
                id="hh-mac-dinh"
                className="o-nhap"
                inputMode="decimal"
                value={macDinh}
                onChange={(e) => setMacDinh(locPt(e.target.value))}
              />
            </div>
            {/* ADR-076 (KH-GĐ2 6.2.3): khách được giảm thì thợ vẫn nhận đủ hay nhận theo giá đã giảm. */}
            <div className="truong" style={{ marginTop: 12 }}>
              <span className="nhan">{S.coSoHoaHong}</span>
              <div className="hang-chip">
                {([true, false] as const).map((v) => (
                  <button
                    key={String(v)}
                    type="button"
                    aria-pressed={truocGiam === v}
                    className={`chip ${truocGiam === v ? 'dang-chon' : ''}`}
                    onClick={() => setTruocGiam(v)}
                  >
                    {v ? S.truocGiam : S.sauGiam}
                  </button>
                ))}
              </div>
              <p className="chu-phu">{truocGiam ? S.truocGiamPhu : S.sauGiamPhu}</p>
            </div>
            <h3 className="tieu-de-nho">{S.rieng}</h3>
            {rieng.map((r, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: dòng nhập chưa có id; thứ tự là danh tính.
              <div key={i} className="dong-muc-tang">
                <div className="truong">
                  <label className="nhan" htmlFor={`hh-dv-${i}`}>
                    {S.chonDichVu}
                  </label>
                  <select
                    id={`hh-dv-${i}`}
                    className="o-chon"
                    value={r.serviceVariantId}
                    onChange={(e) =>
                      setRieng(
                        rieng.map((x, j) =>
                          j === i ? { ...x, serviceVariantId: e.target.value } : x,
                        ),
                      )
                    }
                  >
                    <option value="">{S.chonDichVu}</option>
                    {dv
                      .filter((s) => s.id === r.serviceVariantId || !daChon.has(s.id))
                      .map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="truong o-phan-tram">
                  <label className="nhan" htmlFor={`hh-pt-${i}`}>
                    {S.mucRieng}
                  </label>
                  <input
                    id={`hh-pt-${i}`}
                    className="o-nhap"
                    inputMode="decimal"
                    value={r.pt}
                    onChange={(e) =>
                      setRieng(
                        rieng.map((x, j) => (j === i ? { ...x, pt: locPt(e.target.value) } : x)),
                      )
                    }
                  />
                </div>
                <button
                  type="button"
                  className="nut nut-nhat nut-icon"
                  aria-label={S.boRieng}
                  onClick={() => setRieng(rieng.filter((_, j) => j !== i))}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
            <div className="hang-chip" style={{ marginTop: 12 }}>
              <button
                type="button"
                className="nut nut-vien nut-nho"
                onClick={() => setRieng([...rieng, { serviceVariantId: '', pt: macDinh }])}
              >
                <Plus size={16} />
                {S.themRieng}
              </button>
            </div>
            <div className="chan-the">
              <button type="button" className="nut nut-chinh" onClick={() => void luu()}>
                {S.luuHoaHong}
              </button>
            </div>
          </>
        )}
      </section>
      <BangThang api={api} />
    </div>
  )
}

function BangThang({ api }: { api: Api }) {
  const coChi = useTinhNang('khoan-chi')
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [thang, setThang] = useState(thangTruoc())
  const [bl, setBl] = useState<BangLuong | null>(null)
  const [loi, setLoi] = useState<string | null>(null)
  const [bao, setBao] = useState<string | null>(null)
  const [dang, setDang] = useState(false)
  const [hoiChotThang, setHoiChotThang] = useState(false)
  useEffect(() => {
    setBl(null)
    setLoi(null)
    void api
      .bangLuong(thang)
      .then(setBl)
      .catch((e: unknown) => setLoi(loiCua(e)))
  }, [api, thang])
  const thangNay = ngayVn().slice(0, 7)
  async function chot() {
    setDang(true)
    try {
      setBl(await api.chotBangLuong(thang, khoa))
      lamMoiKhoa()
      setBao(S.daChotXong(`${thang.slice(5)}/${thang.slice(0, 4)}`))
    } catch (e) {
      setLoi(loiCua(e))
    } finally {
      setDang(false)
    }
  }
  const nhanThang = `${thang.slice(5)}/${thang.slice(0, 4)}`
  return (
    <section className="the the-dem">
      <h2>{S.bangLuong}</h2>
      <p className="chu-phu">{S.bangLuongHint}</p>
      {/* Bộ chọn tháng tiếng Việt: `<input type="month">` hiện "August 2026" (ui-critic v11). */}
      <div className="dieu-huong-ngay" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="nut nut-icon"
          aria-label={S.thangTruoc}
          onClick={() => {
            setHoiChotThang(false)
            setThang(doiThang(thang, -1))
          }}
        >
          <ChevronLeft size={22} />
        </button>
        <span className="nhan-ngay">{S.nhanThang(nhanThang)}</span>
        <button
          type="button"
          className="nut nut-icon"
          aria-label={S.thangSau}
          disabled={thang >= thangNay}
          onClick={() => {
            setHoiChotThang(false)
            setThang(doiThang(thang, 1))
          }}
        >
          <ChevronRight size={22} />
        </button>
      </div>
      {bao !== null && (
        <ThongBao loai="thanh-cong" onDong={() => setBao(null)}>
          {bao}
        </ThongBao>
      )}
      {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
      {bl === null ? (
        loi === null && <Xuong cao={80} />
      ) : (
        <>
          {bl.dong.length === 0 ? (
            <p className="chu-phu">{S.chuaCoAi}</p>
          ) : (
            <ul className="ds-so">
              {bl.dong.map((d) => (
                <li key={d.staffId}>
                  <span>
                    <CacCum cac={[d.ten, S.soDichVu(d.soDichVu)]} />
                  </span>
                  <b>{tienVnd(d.tienVnd)}</b>
                </li>
              ))}
              <li className="dong-tong">
                <span>{S.tongCong}</span>
                <b>{tienVnd(bl.tongVnd)}</b>
              </li>
            </ul>
          )}
          {bl.daChot !== null ? (
            <>
              <p className="chu-phu">{S.daChot(lucViet(bl.daChot.luc), bl.daChot.nguoi ?? '')}</p>
              {coChi && bl.daChot.tongVnd !== '0' && (
                <NutHoaHongThang
                  key={thang}
                  api={api}
                  thang={thang}
                  tongVnd={bl.daChot.tongVnd}
                  daGhiVnd={bl.daChot.daGhiChiVnd ?? null}
                />
              )}
            </>
          ) : thang >= thangNay ? (
            <p className="chu-phu">{S.thangDangChay}</p>
          ) : bl.dong.length === 0 ? null : hoiChotThang ? (
            <>
              <ThongBao loai="canh-bao">{S.chotHoiLai(nhanThang, tienVnd(bl.tongVnd))}</ThongBao>
              <div className="hang-chip" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="nut nut-vien"
                  onClick={() => setHoiChotThang(false)}
                >
                  {S.giuLai}
                </button>
                <button
                  type="button"
                  className="nut nut-chinh"
                  style={{ flex: 1 }}
                  disabled={dang}
                  onClick={() => void chot()}
                >
                  {S.chotThat}
                </button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="nut nut-vien"
              style={{ marginTop: 12 }}
              onClick={() => setHoiChotThang(true)}
            >
              {S.chotThang(nhanThang)}
            </button>
          )}
        </>
      )}
    </section>
  )
}
